import React, { useEffect, useState } from "react";
import { api, formatEUR } from "@/lib/api";
import { toast } from "sonner";
import { RotateCcw, AlertTriangle, X, Receipt as ReceiptIcon, Search, Calendar, Printer } from "lucide-react";
import ScontrinoPreview from "@/components/ScontrinoPreview";
import { sendFiscalVoid, sendFiscalRefund, fiscalErrorMessage } from "@/lib/fiscal";

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function Refunds() {
  const [date, setDate] = useState(todayISO());
  const [closedOrders, setClosedOrders] = useState([]);
  const [refunds, setRefunds] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [search, setSearch] = useState("");
  const [refundTarget, setRefundTarget] = useState(null);
  const [last, setLast] = useState(null);
  const [showScontrino, setShowScontrino] = useState(false);
  const [tenant, setTenant] = useState(null);

  useEffect(() => {
    api.get("/refunds/reasons").then(r => setReasons(Array.isArray(r.data.reasons) ? r.data.reasons : []));
    api.get("/tenant/me").then(r => setTenant(r.data));
  }, []);

  useEffect(() => {
    // Get all closed orders today
    api.get(`/orders?limit=300`).then(r => {
      const filtered = (Array.isArray(r.data) ? r.data : []).filter(o =>
        (o.status === "closed_fiscal" || o.status === "closed_non_fiscal") &&
        o.order_type !== "refund" &&
        (o.closed_at || "").startsWith(date)
      );
      setClosedOrders(filtered);
    });
    api.get(`/refunds?date=${date}`).then(r => setRefunds(Array.isArray(r.data) ? r.data : []));
  }, [date]);

  const printIssued = async (r) => {
    try {
      let original = closedOrders.find((o) => o.id === r.original_order_id);
      if (!original && r.original_order_id) {
        const got = await api.get(`/orders/${r.original_order_id}`);
        original = got.data;
      }
      if ((original?.status || r.status) !== "closed_fiscal") {
        setLast(r);
        setShowScontrino(true);
        return;
      }
      const res = original?.refund_full
        ? await sendFiscalVoid({ order: original })
        : await sendFiscalRefund({ order: r });
      if (res?.ok) {
        toast.success(`Inviato alla "${res.printer}"${res.detail ? ` — ${res.detail}` : ""}`);
      } else if (res && res.reason !== "no_fiscal_printer") {
        toast.error(`${fiscalErrorMessage(res)}${res.voidRef ? ` — ${res.voidRef}` : ""}`, { duration: 12000 });
      }
      setLast(r);
      setShowScontrino(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Stampa non riuscita");
    }
  };

  const filtered = (Array.isArray(closedOrders) ? closedOrders : []).filter(o => {
    if (!search) return true;
    const s = search.toLowerCase();
    return String(o.receipt_number || "").includes(s) ||
           (o.operator_name || "").toLowerCase().includes(s) ||
           (o.mode || "").toLowerCase().includes(s);
  });

  return (
    <div className="p-8 lg:p-12">
      <div className="mb-8">
        <div className="text-xs font-mono uppercase tracking-[0.25em] text-velta-mute mb-3">Storni / Resi</div>
        <h1 className="font-heading text-4xl font-bold tracking-tight mb-2">Gestione storni</h1>
        <p className="text-velta-mute">Annulla o stornare parzialmente uno scontrino già emesso con causale tracciata.</p>
      </div>

      {/* Filter bar */}
      <div className="bg-white border border-velta-border rounded-md p-4 mb-6 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 px-3 h-10 bg-velta-bg border border-velta-border rounded-md">
          <Calendar className="w-4 h-4 text-velta-mute" />
          <input data-testid="ref-date" type="date" value={date} onChange={e => setDate(e.target.value)} className="bg-transparent font-mono outline-none" />
        </div>
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-3 text-velta-mute" />
          <input
            data-testid="ref-search"
            placeholder="Cerca per numero, operatore, modalità…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full h-10 pl-9 pr-3 border border-velta-border rounded-md text-sm focus:border-velta-ink outline-none"
          />
        </div>
        <div className="text-xs text-velta-mute">
          <span data-testid="ref-orders-count">{filtered.length}</span> scontrini · <span data-testid="ref-refunds-count">{refunds.length}</span> storni
        </div>
      </div>

      {/* Closed orders list */}
      <div className="bg-white border border-velta-border rounded-md overflow-hidden mb-6">
        <table className="w-full text-sm">
          <thead className="bg-velta-bg border-b border-velta-border text-xs uppercase tracking-widest text-velta-mute">
            <tr>
              <th className="text-left px-4 py-3">N°</th>
              <th className="text-left px-4 py-3">Ora</th>
              <th className="text-left px-4 py-3">Modalità</th>
              <th className="text-left px-4 py-3">Operatore</th>
              <th className="text-right px-4 py-3">Totale</th>
              <th className="text-center px-4 py-3">Storno</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan="7" className="text-center text-velta-mute py-12">Nessuno scontrino in questa data.</td></tr>
            ) : (Array.isArray(filtered) ? filtered : []).map(o => (
              <tr key={o.id} className="border-t border-velta-border hover:bg-velta-bg">
                <td className="px-4 py-3 font-mono font-bold">#{o.receipt_number || "—"}</td>
                <td className="px-4 py-3 font-mono text-xs">{(o.closed_at || "").slice(11, 19)}</td>
                <td className="px-4 py-3 capitalize">{o.mode}</td>
                <td className="px-4 py-3 text-velta-mute text-xs">{o.operator_name || "—"}</td>
                <td className="px-4 py-3 text-right font-mono font-bold">{formatEUR(o.total)}</td>
                <td className="px-4 py-3 text-center">
                  {o.refund_full ? <span className="text-xs bg-velta-error/10 text-velta-error px-2 py-0.5 rounded">Totale</span>
                   : o.has_refund ? <span className="text-xs bg-[#F59E0B]/10 text-[#F59E0B] px-2 py-0.5 rounded">Parziale</span>
                   : <span className="text-xs text-velta-mute">—</span>}
                </td>
                <td className="px-4 py-3 text-right">
                  {!o.refund_full && (
                    <button data-testid={`ref-btn-${o.id}`} onClick={() => setRefundTarget(o)} className="h-8 px-3 text-xs font-medium border border-velta-error/30 text-velta-error rounded-md hover:bg-red-50 inline-flex items-center gap-1">
                      <RotateCcw className="w-3 h-3" /> Storna
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Refunds today list */}
      {refunds.length > 0 && (
        <div>
          <h2 className="text-xs font-mono uppercase tracking-widest text-velta-mute mb-3">Storni emessi oggi</h2>
          <div className="space-y-2">
            {(Array.isArray(refunds) ? refunds : []).map(r => {
              const reason = reasons.find(x => x.id === r.refund_reason);
              return (
                <div key={r.id} data-testid={`ref-row-${r.id}`} className="bg-white border border-velta-error/30 rounded-md p-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <RotateCcw className="w-5 h-5 text-velta-error" />
                    <div>
                      <div className="font-medium text-sm">Storno #{r.refund_number} — Scontrino origine #{r.original_receipt_number}</div>
                      <div className="text-xs text-velta-mute">
                        {reason?.label || r.refund_reason} {r.refund_reason_note && `· ${r.refund_reason_note}`}
                      </div>
                    </div>
                  </div>
                  <div className="text-right flex items-center gap-3">
                    <button type="button" data-testid={`ref-print-${r.id}`} onClick={() => printIssued(r)} className="h-8 px-3 text-xs font-medium border border-velta-border rounded-md inline-flex items-center gap-1 hover:border-velta-ink">
                      <Printer className="w-3 h-3" /> Stampa
                    </button>
                    <div>
                      <div className="font-mono font-bold text-velta-error">{formatEUR(r.total)}</div>
                      <div className="text-xs text-velta-mute">{(r.closed_at || "").slice(11, 19)}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {refundTarget && (
        <RefundModal
          order={refundTarget}
          reasons={reasons}
          onClose={() => setRefundTarget(null)}
          onDone={async (refundDoc, full) => {
            const original = refundTarget;
            setRefundTarget(null);
            api.get(`/refunds?date=${date}`).then(r => setRefunds(Array.isArray(r.data) ? r.data : []));
            api.get(`/orders?limit=300`).then(r => {
              setClosedOrders((Array.isArray(r.data) ? r.data : []).filter(o =>
                (o.status === "closed_fiscal" || o.status === "closed_non_fiscal") &&
                o.order_type !== "refund" &&
                (o.closed_at || "").startsWith(date)
              ));
            });
            if (original?.status === "closed_fiscal") {
              const res = full
                ? await sendFiscalVoid({ order: original })
                : await sendFiscalRefund({ order: refundDoc });
              if (res?.ok) {
                toast.success(full
                  ? `Annullo inviato alla "${res.printer}"${res.detail ? ` — ${res.detail}` : ""}`
                  : `Reso inviato alla "${res.printer}"${res.detail ? ` — ${res.detail}` : ""}`);
                if (res.rt && refundDoc?.id) api.post(`/orders/${refundDoc.id}/rt-info`, res.rt).catch(() => {});
              } else if (res && res.reason !== "no_fiscal_printer") {
                toast.error(`${fiscalErrorMessage(res)}${res.voidRef ? ` — ${res.voidRef}` : ""}`, { duration: 12000 });
              }
            }
            setLast(refundDoc); setShowScontrino(true);
          }}
        />
      )}

      {showScontrino && <ScontrinoPreview order={last} tenant={tenant} fiscal onClose={() => setShowScontrino(false)} />}
    </div>
  );
}

function RefundModal({ order, reasons, onClose, onDone }) {
  const [mode, setMode] = useState("total"); // total | partial
  const [selectedLines, setSelectedLines] = useState({}); // { idx: qty }
  const [reason, setReason] = useState("difetto_prodotto");
  const [reasonNote, setReasonNote] = useState("");
  const [busy, setBusy] = useState(false);

  const lines = order.lines || [];
  const calcPartialTotal = () => {
    let s = 0;
    Object.entries(selectedLines).forEach(([idx, qty]) => {
      const l = lines[idx];
      if (l && qty > 0) s += l.price * qty;
    });
    return s;
  };

  const submit = async () => {
    if (reason === "altro" && !reasonNote.trim()) return toast.error("Specifica il motivo");
    let payload = { reason, reason_note: reasonNote || undefined };
    if (mode === "partial") {
      const sel = Object.entries(selectedLines).filter(([, q]) => q > 0).map(([idx, qty]) => ({ line_index: parseInt(idx), qty }));
      if (sel.length === 0) return toast.error("Seleziona almeno una riga");
      payload.lines = sel;
    }
    setBusy(true);
    try {
      const r = await api.post(`/orders/${order.id}/refund`, payload);
      toast.success(`Storno #${r.data.refund_number} emesso`);
      onDone(r.data, mode === "total");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-md w-full max-w-2xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-velta-border flex items-center justify-between">
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-velta-mute">Storno scontrino</div>
            <h3 className="font-heading text-2xl font-semibold">#{order.receipt_number} — {formatEUR(order.total)}</h3>
          </div>
          <button data-testid="ref-modal-close" onClick={onClose}><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-auto p-5 space-y-4">
          {/* Mode toggle */}
          <div className="grid grid-cols-2 gap-2">
            <button
              data-testid="ref-mode-total"
              onClick={() => setMode("total")}
              className={`h-12 rounded-md font-medium border-2 ${mode === "total" ? "bg-velta-error text-white border-velta-error" : "bg-white border-velta-border"}`}
            >
              Storno totale ({formatEUR(order.total)})
            </button>
            <button
              data-testid="ref-mode-partial"
              onClick={() => setMode("partial")}
              className={`h-12 rounded-md font-medium border-2 ${mode === "partial" ? "bg-[#F59E0B] text-white border-[#F59E0B]" : "bg-white border-velta-border"}`}
            >
              Storno parziale
            </button>
          </div>

          {mode === "partial" && (
            <div className="space-y-2">
              <div className="text-xs font-mono uppercase tracking-widest text-velta-mute">Seleziona righe da stornare</div>
              {(Array.isArray(lines) ? lines : []).map((l, idx) => {
                const maxQty = Math.abs(l.qty);
                const curQty = selectedLines[idx] || 0;
                return (
                  <div key={idx} className="bg-velta-bg rounded-md p-3 flex items-center gap-3">
                    <input
                      type="checkbox"
                      data-testid={`ref-line-${idx}`}
                      checked={curQty > 0}
                      onChange={e => setSelectedLines({ ...selectedLines, [idx]: e.target.checked ? maxQty : 0 })}
                    />
                    <div className="flex-1">
                      <div className="font-medium text-sm">{l.name}</div>
                      <div className="text-xs text-velta-mute">{formatEUR(l.price)} × max {maxQty}</div>
                    </div>
                    {curQty > 0 && (
                      <input
                        type="number"
                        data-testid={`ref-qty-${idx}`}
                        value={curQty}
                        min="1"
                        max={maxQty}
                        step="0.5"
                        onChange={e => setSelectedLines({ ...selectedLines, [idx]: Math.min(maxQty, Math.max(0, parseFloat(e.target.value) || 0)) })}
                        className="w-16 h-8 px-2 border border-velta-border rounded text-center font-mono"
                      />
                    )}
                    <div className="w-20 text-right font-mono font-bold text-velta-error">
                      {curQty > 0 ? `-${formatEUR(l.price * curQty)}` : ""}
                    </div>
                  </div>
                );
              })}
              <div className="text-right font-mono font-bold pt-2 border-t border-velta-border">
                Totale storno: <span data-testid="ref-partial-total" className="text-velta-error">-{formatEUR(calcPartialTotal())}</span>
              </div>
            </div>
          )}

          {/* Causale */}
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Causale *</label>
            <div className="grid grid-cols-2 gap-2">
              {(Array.isArray(reasons) ? reasons : []).map(r => (
                <label
                  key={r.id}
                  data-testid={`ref-reason-${r.id}`}
                  className={`p-3 border rounded-md text-sm cursor-pointer ${reason === r.id ? "bg-velta-ink text-white border-velta-ink" : "bg-white border-velta-border hover:border-velta-ink"}`}
                >
                  <input type="radio" name="reason" value={r.id} checked={reason === r.id} onChange={() => setReason(r.id)} className="sr-only" />
                  {r.label}
                </label>
              ))}
            </div>
          </div>

          {reason === "altro" && (
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Specifica motivo *</label>
              <input
                data-testid="ref-reason-note"
                value={reasonNote}
                onChange={e => setReasonNote(e.target.value)}
                placeholder="es. Errore di battitura, cliente cambiato idea, ..."
                className="w-full h-11 px-3 border border-velta-border rounded-md text-sm"
              />
            </div>
          )}

          <div className="flex items-start gap-2 bg-yellow-50 border border-yellow-200 rounded-md p-3 text-xs">
            <AlertTriangle className="w-4 h-4 text-yellow-700 mt-0.5 shrink-0" />
            <div>
              <strong>Attenzione</strong>: lo storno crea un nuovo scontrino fiscale con totale negativo. Operazione non reversibile.
            </div>
          </div>
        </div>

        <div className="p-5 border-t border-velta-border bg-velta-bg flex gap-3">
          <button onClick={onClose} className="flex-1 h-12 border border-velta-border rounded-md font-medium">Annulla</button>
          <button
            data-testid="ref-submit"
            onClick={submit}
            disabled={busy}
            className="flex-1 h-12 bg-velta-error text-white rounded-md font-semibold btn-tactile disabled:opacity-40 inline-flex items-center justify-center gap-2"
          >
            <ReceiptIcon className="w-4 h-4" />
            {busy ? "Storno in corso…" : "Emetti scontrino di storno"}
          </button>
        </div>
      </div>
    </div>
  );
}
